import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { ModalClose } from '../icons'
import { useConfirm } from '../../contexts/ConfirmContext'

const PAGE_SIZE = 20

export default function AllowedEmailDomainsModal({ open, onClose, onChanged }) {
  const confirm = useConfirm()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [q, setQ] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [newLabel, setNewLabel] = useState('')

  const load = useCallback(async () => {
    if (!open) return
    setLoading(true)
    try {
      const res = await axios.get('/api/admin/allowed-email-domains', {
        params: { page, pageSize: PAGE_SIZE, q: q || undefined },
      })
      setItems(res.data.items || [])
      setTotal(res.data.total || 0)
      setTotalPages(res.data.totalPages || 1)
    } catch {
      toast.error('Failed to load allowed domains')
    } finally {
      setLoading(false)
    }
  }, [open, page, q])

  useEffect(() => {
    if (!open) return undefined
    load()
  }, [open, load])

  useEffect(() => {
    if (!open) return undefined
    const timer = window.setTimeout(() => {
      setPage(1)
      setQ(searchInput.trim())
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchInput, open])

  const handleAdd = async (e) => {
    e.preventDefault()
    const domain = newDomain.trim()
    if (!domain) return
    setSaving(true)
    try {
      await axios.post('/api/admin/allowed-email-domains', {
        domain,
        label: newLabel.trim() || undefined,
      })
      toast.success('Domain added')
      setNewDomain('')
      setNewLabel('')
      setPage(1)
      setQ('')
      setSearchInput('')
      await load()
      onChanged?.()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add domain')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row) => {
    const ok = await confirm({
      title: 'Delete allowed domain?',
      message: `Remove @${row.domain} from the registration whitelist?`,
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await axios.delete(`/api/admin/allowed-email-domains/${row.id}`)
      toast.success('Domain deleted')
      await load()
      onChanged?.()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete domain')
    }
  }

  if (!open) return null

  const start = total ? (page - 1) * PAGE_SIZE + 1 : 0
  const end = Math.min(page * PAGE_SIZE, total)

  return (
    <div className="admin-modal-overlay show" role="presentation" onClick={onClose}>
      <div className="admin-modal admin-modal-wide admin-modal-scroll" role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>Allowed Corporate Email Domains ({total.toLocaleString()})</h3>
          <ModalClose onClick={onClose} />
        </div>
        <div className="admin-modal-body">
          <p style={{ marginTop: 0, color: '#606266', fontSize: 13 }}>
            Only email addresses matching these domain suffixes can register. Domains are stored in the database and enforced at registration.
          </p>

          <form onSubmit={handleAdd} className="admin-filter-bar" style={{ marginBottom: 16 }}>
            <input
              className="admin-filter-search"
              placeholder="Add domain, e.g. sequoiacap.com or @sequoiacap.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              required
            />
            <input
              className="admin-filter-search"
              placeholder="Label (optional)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <button type="submit" className="admin-btn primary" disabled={saving}>
              {saving ? 'Adding…' : 'Add Domain'}
            </button>
          </form>

          <div className="admin-filter-bar" style={{ marginBottom: 12 }}>
            <input
              className="admin-filter-search"
              placeholder="Search domains…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className="admin-table-card embedded">
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Label</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#909399' }}>Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#909399' }}>No domains found</td></tr>
                ) : items.map((row) => (
                  <tr key={row.id}>
                    <td><code>@{row.domain}</code></td>
                    <td>{row.label || '—'}</td>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <button type="button" className="admin-btn sm danger" onClick={() => handleDelete(row)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination" style={{ marginTop: 16 }}>
            <div className="admin-pagination-info">
              Showing {start}–{end} of {total.toLocaleString()} domains
            </div>
            <div className="admin-pagination-controls">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span style={{ padding: '0 8px', fontSize: 13 }}>Page {page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          </div>
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
